import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Sparkles } from 'lucide-react';
import { useDeviceStore } from '../../stores/deviceStore';
import { DeviceCard } from '../devices/DeviceCard';

export function DevicesScreen() {
  const navigate = useNavigate();
  const { devices, fetchDevices, isLoading } = useDeviceStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const rooms = [...new Set(devices.map(d => d.room).filter(Boolean))];
  
  const filteredDevices = devices.filter(device => {
    const matchesSearch = !searchQuery || 
      device.customName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.baseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.room?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRoom = !selectedRoom || device.room === selectedRoom;
    
    return matchesSearch && matchesRoom;
  });

  const onlineCount = devices.filter(d => d.isOnline).length;
  const activeCount = devices.filter(d => {
    const power = d.parameters.find(p => p.name === 'power');
    return power?.boolValue;
  }).length;

  return (
    <div className="min-h-screen pb-24">
      <div className="glass sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gradient">Мой Дом</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                {onlineCount} из {devices.length} онлайн
                {activeCount > 0 && ` · ${activeCount} активно`}
              </p>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/pairing')}
              className="w-11 h-11 gradient-primary rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30"
            >
              <Plus className="w-5 h-5 text-white" />
            </motion.button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Поиск устройств..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-12 pl-12 pr-4 bg-white/60 backdrop-blur-lg border border-white/50 rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500/30 transition-all"
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6">
        {rooms.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide -mx-5 px-5">
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setSelectedRoom(null)}
              className={`
                px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all
                ${!selectedRoom 
                  ? 'gradient-primary text-white shadow-md shadow-indigo-500/20' 
                  : 'bg-white/60 text-slate-600 border border-white/50'
                }
              `}
            >
              Все
            </motion.button>
            {rooms.map(room => (
              <motion.button
                key={room}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelectedRoom(room)}
                className={`
                  px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all
                  ${selectedRoom === room 
                    ? 'gradient-primary text-white shadow-md shadow-indigo-500/20' 
                    : 'bg-white/60 text-slate-600 border border-white/50'
                  }
                `}
              >
                {room}
              </motion.button>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="glass-card p-5 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-slate-200/50 rounded-2xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200/50 rounded w-1/2" />
                    <div className="h-3 bg-slate-200/50 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredDevices.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {searchQuery ? 'Ничего не найдено' : 'Нет устройств'}
            </h3>
            <p className="text-slate-400 mb-6">
              {searchQuery 
                ? 'Попробуйте изменить запрос' 
                : 'Добавьте первое устройство для начала'}
            </p>
            {!searchQuery && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/pairing')}
                className="gradient-primary text-white px-6 py-3 rounded-xl font-medium shadow-lg shadow-indigo-500/30"
              >
                Добавить устройство
              </motion.button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {filteredDevices.map((device, index) => (
                <DeviceCard key={device.deviceId} device={device} index={index} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
